import { Link } from 'react-router-dom';
import type { FaqItem } from './faqTypes';

export const faqItemsEn: FaqItem[] = [
  {
    q: 'What does “missed connection” actually mean?',
    a: (
      <>
        <p>
          A missed connection is a fleeting encounter between two people when a moment clearly
          mattered—but no contact was made.
        </p>
        <p>
          Maybe a glance.
          <br />
          A smile.
          <br />
          A sense of “something was there.”
        </p>
        <p>
          <strong>I Think It’s You :|:</strong> gives those moments a second chance.
        </p>
      </>
    ),
  },
  {
    q: 'How does I Think It’s You work, in practice?',
    a: (
      <>
        <p>It’s simple:</p>
        <ol className="faq-answer-list faq-answer-list--numbered">
          <li>You remember an encounter</li>
          <li>You describe it as precisely as you can</li>
          <li>You publish your post</li>
        </ol>
        <p>Others can read it—and if someone recognizes themselves, contact can happen.</p>
        <p>
          👉{' '}
          <a href="https://www.i-think-its-you.at" target="_blank" rel="noopener noreferrer">
            www.i-think-its-you.at
          </a>
        </p>
      </>
    ),
  },
  {
    q: 'What should I put in my post?',
    a: (
      <>
        <p>The more specific, the better:</p>
        <ul className="faq-answer-list">
          <li>Place (e.g. café, event, street)</li>
          <li>Time</li>
          <li>Description of the person</li>
          <li>What happened</li>
          <li>Why the moment stayed with you</li>
        </ul>
        <p>
          <strong>👉 Important: Be respectful, honest, and do not share private data.</strong>
        </p>
      </>
    ),
  },
  {
    q: 'Is this anonymous?',
    a: (
      <>
        <p>Yes.</p>
        <p>You decide how much you reveal.</p>
        <p>Discretion is part of the concept.</p>
      </>
    ),
  },
  {
    q: 'Is I Think It’s You a dating platform?',
    a: (
      <>
        <p>No—and that’s exactly the difference.</p>
        <p>It’s not about profiles, matching, or algorithms.</p>
        <p>It’s about real encounters in real life.</p>
        <p>
          👉 <strong>I Think It’s You</strong> starts where other platforms stop.
        </p>
      </>
    ),
  },
  {
    q: 'Does this really work?',
    a: (
      <>
        <p>Yes.</p>
        <p>
          <strong>Missed-connection platforms</strong> have existed around the world for many years—and
          people do find each other this way, again and again.
        </p>
        <p>Not every trail leads somewhere.</p>
        <p>But some do.</p>
      </>
    ),
  },
  {
    q: 'Why does a platform like this exist at all?',
    a: (
      <>
        <p>Because many encounters slip away too fast:</p>
        <ul className="faq-answer-list">
          <li>you don’t dare to speak up</li>
          <li>the moment isn’t right</li>
          <li>you only realize it later</li>
        </ul>
        <p>
          <strong>I Think It’s You :|:</strong> turns those moments into possibilities.
        </p>
      </>
    ),
  },
  {
    q: 'Is this only for single people?',
    a: (
      <>
        <p>No.</p>
        <p>It’s not only about romantic encounters, but about:</p>
        <ul className="faq-answer-list">
          <li>warmth and rapport</li>
          <li>curiosity</li>
          <li>human connection</li>
        </ul>
      </>
    ),
  },
  {
    q: 'Can I find someone I only saw briefly?',
    a: (
      <>
        <p>Yes—that’s what the platform is for.</p>
        <p>Even a fleeting meeting can be enough if the description is precise.</p>
      </>
    ),
  },
  {
    q: 'What if nobody responds?',
    a: (
      <>
        <p>Then your post remains a trail.</p>
        <p>Not every trail is resolved.</p>
        <p>But each one tells a story.</p>
        <p>And sometimes the right person reads it at the right time.</p>
      </>
    ),
  },
  {
    q: 'Are there rules for using the site?',
    a: (
      <>
        <p>Yes—and they matter:</p>
        <ul className="faq-answer-list">
          <li>treat others with respect</li>
          <li>no abusive content</li>
          <li>no sensitive or identifying personal data</li>
          <li>no harassment</li>
        </ul>
        <p>
          <strong>👉 I Think It’s You is a space for positive encounters.</strong>
        </p>
      </>
    ),
  },
  {
    q: 'Where does the idea of missed connections come from?',
    a: (
      <>
        <p>
          <strong>Missed connections</strong> grew out of classic personals and later online boards.
        </p>
        <p>
          People have long tried to reconnect after a moment slipped away—through newspapers, notice
          boards, or digital listings.
        </p>
        <p>
          <strong>I Think It’s You :|:</strong> continues that idea—contemporary, emotional, and tied to
          real-life experience.
        </p>
      </>
    ),
  },
  {
    q: 'What makes I Think It’s You special?',
    a: (
      <ul className="faq-answer-list">
        <li>Ties to the real world (events, everyday life, encounters)</li>
        <li>Part of the Crime Club universe</li>
        <li>The :|: symbol as a quiet signal</li>
        <li>Focus on real moments, not profiles</li>
      </ul>
    ),
  },
  {
    q: 'How can I improve my chances?',
    a: (
      <>
        <ul className="faq-answer-list">
          <li>be specific (place, time, situation)</li>
          <li>describe the moment, not only the person</li>
          <li>write authentically</li>
        </ul>
        <p>
          <strong>👉 People remember feelings, not data points.</strong>
        </p>
      </>
    ),
  },
  {
    q: 'Can I post more than once?',
    a: (
      <>
        <p>Yes—each encounter is its own case.</p>
        <p>And each one deserves attention.</p>
      </>
    ),
  },
  {
    q: 'Is it safe?',
    a: (
      <>
        <p>Yes—when used responsibly.</p>
        <p>The platform is designed to keep encounters respectful and anonymous.</p>
      </>
    ),
  },
  {
    q: 'What is the signal?',
    a: (
      <>
        <p>
          Show the signal on your phone to someone you posted a sighting about. Maybe they’ll notice and
          think to visit <strong>I Think It’s You</strong>.
        </p>
        <p>Maybe.</p>
      </>
    ),
  },
  {
    q: 'What if I saw someone on a plane or a train?',
    a: (
      <p>
        In general, post a sighting for both the departure location and the arrival location. You need
        two posts, because a sighting can only be tied to one place at a time.
      </p>
    ),
  },
  {
    q: 'Why does I Think It’s You need my location?',
    a: (
      <>
        <p>
          <strong>I Think It’s You</strong> is a location-based messaging service.
        </p>
        <p>
          We need the place where you saw someone so that person—or others nearby—can find the post.
        </p>
        <p>
          If you saw someone in Paris, set the sighting location in Paris. You can also browse sightings
          in your city so you don’t have to sift through everything.
        </p>
      </>
    ),
  },
  {
    q: 'Why doesn’t “determine position” work?',
    a: (
      <>
        <p>Several things can cause this:</p>
        <ul className="faq-answer-list">
          <li>Your device may not support GPS.</li>
          <li>
            You may get an error if Bluetooth is off and/or you’re not connected to Wi‑Fi (depending on
            how location is resolved).
          </li>
          <li>You may be in an area with no mobile signal.</li>
        </ul>
        <p>If any of that applies, use “change city” to navigate manually.</p>
      </>
    ),
  },
  {
    q: 'I deleted a sighting by mistake. Can I restore it?',
    a: <p>Unfortunately, no. You’ll need to enter it again.</p>,
  },
  {
    q: 'How long are my sightings kept?',
    a: <p>Indefinitely—until you delete them.</p>,
  },
  {
    q: 'Why didn’t I get my confirmation or password-reset email?',
    a: (
      <>
        <p>Please check your spam folder first.</p>
        <p>
          Also add{' '}
          <a href="mailto:noreply@i-think-its-you.at">noreply@i-think-its-you.at</a> to your contacts.
        </p>
        <p>
          If that doesn’t help, use our <Link to="/contact">contact form</Link> for help and we’ll follow
          up on your registration.
        </p>
      </>
    ),
  },
  {
    q: 'Should I put my contact details in my sighting?',
    a: (
      <>
        <p>No.</p>
        <p>That only exposes you to spam or fraud.</p>
        <p>
          Members use the “Chat” button to reach others and reply to a sighting.
        </p>
      </>
    ),
  },
];
